import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Phone, Mail, Linkedin, MessageCircle } from 'lucide-react';
import { CLINIC_INFO } from '../constants/clinicData';

const ICON_SIZE = 22;

const DOCTOR_CONTACT_INFO = [
  { icon: Phone, label: 'Phone', value: '+91-8668441849', link: 'tel:+918668441849' },
  { icon: Mail, label: 'Email', value: 'drnikhiltonge@gmail.com', link: 'mailto:drnikhiltonge@gmail.com' },
  { icon: Linkedin, label: 'LinkedIn', value: 'drnikhiltonge', link: 'https://www.linkedin.com/in/drnikhiltonge' }
];

const CLINIC_CONTACT_INFO = [
  { icon: MapPin, label: 'Location', value: 'Chandrapur, Maharashtra', link: null },
  { icon: Phone, label: 'Phone', value: '+91-9552374093, 07172-456926, +91-8668441849', link: 'tel:+919552374093' },
  { icon: Mail, label: 'Email', value: CLINIC_INFO.email, link: `mailto:${CLINIC_INFO.email}` },
  { icon: Linkedin, label: 'LinkedIn', value: 'drnikhiltonge', link: 'https://www.linkedin.com/in/drnikhiltonge' }
];

const ContactInfoItem = ({ info }) => {
  const Icon = info.icon;
  return (
    <div className="flex items-start gap-4 transition-transform duration-300 hover:translate-x-2">
      <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5e9' }}>
        <Icon style={{ color: 'var(--medical-primary)' }} size={ICON_SIZE} />
      </div>
      <div>
        <p className="text-sm text-slate-600 mb-1">{info.label}</p>
        {info.link ? (
          <a
            href={info.link}
            target={info.link.startsWith('http') ? '_blank' : undefined}
            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
            className="font-medium text-slate-900 transition-colors"
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--medical-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = ''}
          >
            {info.value}
          </a>
        ) : (
          <p className="font-medium text-slate-900">{info.value}</p>
        )}
      </div>
    </div>
  );
};

const WhatsAppCTA = ({ isDoctorProfile }) => {
  const handleWhatsAppClick = () => {
    const phoneNumber = isDoctorProfile ? '918668441849' : CLINIC_INFO.phoneFormatted;
    const message = isDoctorProfile
      ? 'Hello Dr. Tonge, I would like to get in touch with you.'
      : 'Hello, I would like to get in touch with Tonge Healthcare.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="text-center scroll-reveal" style={{ animationDelay: '0.3s' }}>
      <p className="text-lg text-slate-700 mb-6">
        Ready to schedule an appointment or have questions? Reach out via WhatsApp for quick response.
      </p>
      <Button
        onClick={handleWhatsAppClick}
        size="lg"
        className="text-white px-8 py-6 text-lg shadow-lg"
        style={{ backgroundColor: 'var(--medical-primary)' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--medical-primary-hover)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--medical-primary)'}
        data-testid="whatsapp-contact-btn"
      >
        <MessageCircle className="mr-2" size={ICON_SIZE} />
        Reach Out on WhatsApp
      </Button>
    </div>
  );
};

const ContactSection = ({ isDoctorProfile = false }) => {
  const sectionRef = useScrollReveal();
  const contactInfo = isDoctorProfile ? DOCTOR_CONTACT_INFO : CLINIC_CONTACT_INFO;

  return (
    <section id="contact" className="py-20 px-4 bg-slate-50" ref={sectionRef}>
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 text-center scroll-reveal">
          Get In Touch
        </h2>
        <div className="w-20 h-1 mx-auto mb-12 scroll-reveal" style={{ backgroundColor: 'var(--medical-primary)', animationDelay: '0.1s' }}></div>

        <div className="max-w-3xl mx-auto">
          <Card className="shadow-lg mb-8 scroll-reveal-scale" style={{ borderColor: '#e8f5e9', animationDelay: '0.2s' }}>
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-6">
                {contactInfo.map((info) => (
                  <ContactInfoItem key={info.label} info={info} />
                ))}
              </div>
            </CardContent>
          </Card>

          <WhatsAppCTA isDoctorProfile={isDoctorProfile} />
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
